import * as THREE from 'three';
import maplibregl from 'maplibre-gl';
import { Task, WAYPOINT_COLORS } from '../../types';

export class Task3DLayerAbsolute {
  private id: string;
  private task: Task;
  private camera: THREE.Camera;
  private scene: THREE.Scene;
  private renderer: THREE.WebGLRenderer | null = null;
  private map: maplibregl.Map | null = null;
  private waypoints: Array<{
    position: [number, number];
    radius: number;
    color: string;
    name: string;
    height: number;
    mesh?: THREE.Mesh;
  }> = [];

  constructor(id: string, task: Task) {
    this.id = id;
    this.task = task;
    this.camera = new THREE.Camera();
    this.scene = new THREE.Scene();

    console.log('[Task3DLayerAbsolute] Constructor called with task:', task);
  }

  getLayer() {
    const self = this;

    return {
      id: this.id,
      type: 'custom' as const,
      renderingMode: '3d' as const,

      onAdd(map: maplibregl.Map, gl: WebGLRenderingContext) {
        console.log('[Task3DLayerAbsolute] onAdd called');
        self.map = map;

        // Create Three.js scene
        self.camera = new THREE.Camera();
        self.scene = new THREE.Scene();

        // Add lighting
        const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
        directionalLight.position.set(0, -70, 100).normalize();
        self.scene.add(directionalLight);

        const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
        self.scene.add(ambientLight);

        // Prepare waypoint data
        self.prepareWaypoints();

        // Set up renderer using MapLibre's canvas
        self.renderer = new THREE.WebGLRenderer({
          canvas: map.getCanvas(),
          context: gl,
          antialias: true
        });
        self.renderer.autoClear = false;
      },

      render(gl: WebGLRenderingContext, matrix: number[] | any) {
        if (!self.map || !self.renderer || self.waypoints.length === 0) return;

        // Handle both old (matrix array) and new (args object) API
        const args = Array.isArray(matrix) ? { projectionMatrix: matrix } : matrix;
        const projectionMatrix = args.defaultProjectionData?.mainMatrix || args.projectionMatrix || args;

        // Set up base camera projection
        self.camera.projectionMatrix = new THREE.Matrix4().fromArray(projectionMatrix);

        // Clear previous frame's objects
        self.scene.clear();

        // Re-add lighting after clear
        const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
        directionalLight.position.set(0, -70, 100).normalize();
        self.scene.add(directionalLight);

        const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
        self.scene.add(ambientLight);

        // Create cylinders at each waypoint's absolute position
        self.waypoints.forEach((waypoint, index) => {
          // Convert waypoint coordinates to Mercator
          const modelAsMercatorCoordinate = maplibregl.MercatorCoordinate.fromLngLat(
            waypoint.position,
            0 // Ground level
          );

          // Create cylinder at this position
          const scale = modelAsMercatorCoordinate.meterInMercatorCoordinateUnits();

          // Create cylinder geometry in meters
          const geometry = new THREE.CylinderGeometry(
            waypoint.radius * scale,
            waypoint.radius * scale,
            waypoint.height * scale,
            32,
            1,
            false
          );

          // Material for the cylinder - very subtle
          const material = new THREE.MeshPhongMaterial({
            color: waypoint.color,
            transparent: true,
            opacity: 0.15,  // Very transparent
            side: THREE.DoubleSide,
            depthWrite: false
          });

          const cylinder = new THREE.Mesh(geometry, material);

          // Position cylinder at waypoint location
          cylinder.position.x = modelAsMercatorCoordinate.x;
          cylinder.position.y = modelAsMercatorCoordinate.y;
          cylinder.position.z = modelAsMercatorCoordinate.z + (waypoint.height * scale) / 2;

          // Rotate to stand upright in MapLibre's coordinate system
          cylinder.rotation.x = Math.PI / 2;

          self.scene.add(cylinder);

          // Add subtle wireframe
          const wireframeGeometry = new THREE.CylinderGeometry(
            waypoint.radius * scale,
            waypoint.radius * scale,
            waypoint.height * scale,
            32,
            1,
            false
          );
          const wireframeMaterial = new THREE.MeshBasicMaterial({
            color: waypoint.color,
            wireframe: true,
            transparent: true,
            opacity: 0.05  // Barely visible wireframe
          });
          const wireframe = new THREE.Mesh(wireframeGeometry, wireframeMaterial);
          wireframe.position.copy(cylinder.position);
          wireframe.rotation.copy(cylinder.rotation);
          self.scene.add(wireframe);

          // Add ring at top
          const ringGeometry = new THREE.RingGeometry(
            waypoint.radius * scale * 0.9,
            waypoint.radius * scale,
            32
          );
          const ringMaterial = new THREE.MeshBasicMaterial({
            color: waypoint.color,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.3  // Subtle ring
          });
          const ring = new THREE.Mesh(ringGeometry, ringMaterial);
          ring.position.x = modelAsMercatorCoordinate.x;
          ring.position.y = modelAsMercatorCoordinate.y;
          ring.position.z = modelAsMercatorCoordinate.z + waypoint.height * scale;
          self.scene.add(ring);

          // Debug log for first waypoint
          if (index === 0 && !(self as any).hasLoggedPosition) {
            console.log('[Task3DLayerAbsolute] First waypoint:', {
              lngLat: waypoint.position,
              mercator: { x: modelAsMercatorCoordinate.x, y: modelAsMercatorCoordinate.y, z: modelAsMercatorCoordinate.z },
              scale: scale,
              radius: waypoint.radius,
              scaledRadius: waypoint.radius * scale
            });
            (self as any).hasLoggedPosition = true;
          }
        });

        // Add connecting line
        if (self.waypoints.length > 1) {
          const points: THREE.Vector3[] = [];

          self.waypoints.forEach(waypoint => {
            const mercatorCoord = maplibregl.MercatorCoordinate.fromLngLat(
              waypoint.position,
              waypoint.height / 2 // Mid-height
            );
            points.push(new THREE.Vector3(
              mercatorCoord.x,
              mercatorCoord.y,
              mercatorCoord.z
            ));
          });

          const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
          const lineMaterial = new THREE.LineBasicMaterial({
            color: 0xffff00,
            linewidth: 2,
            transparent: true,
            opacity: 0.3  // Subtle connecting line
          });

          const line = new THREE.Line(lineGeometry, lineMaterial);
          self.scene.add(line);
        }

        // Reset state and render
        self.renderer.resetState();
        self.renderer.render(self.scene, self.camera);

        // Request repaint to keep rendering
        if (self.map) {
          self.map.triggerRepaint();
        }
      },

      onRemove() {
        if (self.renderer) {
          self.renderer.dispose();
        }
        self.scene.clear();
      }
    };
  }

  private prepareWaypoints() {
    console.log('[Task3DLayerAbsolute] Preparing waypoints');

    this.waypoints = this.task.waypoints.map(waypoint => ({
      position: waypoint.coordinates as [number, number],
      radius: waypoint.radius,
      color: WAYPOINT_COLORS[waypoint.type] || '#808080',
      name: waypoint.name,
      height: 2000 // 2km height - less imposing
    }));

    console.log('[Task3DLayerAbsolute] Prepared', this.waypoints.length, 'waypoints');
  }

  updateTask(task: Task) {
    this.task = task;
    this.prepareWaypoints();
    if (this.map) {
      this.map.triggerRepaint();
    }
  }

  setVisibility(visible: boolean) {
    this.scene.visible = visible;
    if (this.map) {
      this.map.triggerRepaint();
    }
  }
}